import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Region } from 'clickNgo/configs/geo'
import { OnboardingResponseStatus } from 'clickNgo/configs/service/onboarding'
import { ContactWorkflowStatus, IContact, LoadingState } from 'clickNgo/entities/contact'
import { mapContactModifiedValues } from 'clickNgo/entities/contact/api/mapping/mapContactModifiedValues'
import { replacementContact } from 'clickNgo/entities/contact/api/replacementContact'
import { mapPartnerFromPartnerAllDataRegions } from 'clickNgo/entities/partner/api/mapping'
import { ContactRelinkingValidationMessage } from 'clickNgo/features/contact/contactRelinking'
import { getJSendErrorMessages } from 'clickNgo/helpers/api'
import { useAppSource } from 'clickNgo/hooks/utils/useAppSource'
import { I375Form } from 'clickNgo/models/crud/forms'
import { Nullable } from 'clickNgo/models/utils'
import {
  FormDropdown,
  FormInput,
  resetAllModifiedDataByKey,
  resetModifiedDataByField,
  updateInvalidData,
  useGetFormData,
  useGetFormDataField,
} from 'cng-common/features/form'
import { FieldWrapperWithChanges } from 'cng-common/features/form/components'
import { ValidationType } from 'cng-common/features/form/configs'
import { FormValidationBuilder } from 'cng-common/features/form/utils/validation'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'
import { IForm } from 'core-components/classes/models/IForm'
import { contactApi } from 'onboarding/features/api/contactApi'
import { FieldGroup } from 'shared/components/fieldGroup'

import { ContactEditorFormKey, ContactEditorFormKeys } from '../../../configs'
import { useReadOnlyMenuItem } from '../../../hooks/useReadOnlyMenuItem'
import {
  ContactEditorSettingFormFields,
  TContactEditorData,
  TContactEditorHandles,
  THandlerResult,
} from '../../../models'
import { ContactEditableContainer, IOnApplyProps } from '../../contactEditableContainer'

import { checkCriticalRelinkingPoints } from './helpers/checkCriticalRelinkingPoints'
import { getPartnerIdSource } from './helpers/getPartnerIdSource'

import styles from './WrappedContactRelinking.module.scss'

interface TProps
  extends Pick<TContactEditorHandles, 'onContactUpdate'>,
    Pick<TContactEditorData, 'contact' | 'contactFlags'> {
  formKey?: ContactEditorFormKey
  source?: string
  requestContact: IContact
}

const PARTNER_FIELDS_TO_SEARCH = ['PartnerID', 'CommunicationName']
/*
 * TODO: Fix label and fields
 * */
export const WrappedOnboardingContactRelinking = memo<TProps>(
  ({
    contact,
    requestContact,
    contactFlags,
    onContactUpdate,
    formKey = ContactEditorFormKeys.contactRelinking,
    source,
  }) => {
    const dispatch = useAppDispatch()
    const appSource = useAppSource(source)
    const { isCurrentMenuItemReadOnly } = useReadOnlyMenuItem()
    const [relinkingCriticalMessage, setRelinkingCriticalMessage] = useState<Nullable<string>>(null)
    const [actualData, updateActualData] = useGetFormDataField<TContactEditorData>(
      ContactEditorFormKeys.settings,
      ContactEditorSettingFormFields.ActualData
    )

    const isOnboardingVariant = formKey === ContactEditorFormKeys.onboardingContactRelinking

    const [errorMessages, setErrorMessages] = useGetFormDataField<string[]>(
      ContactEditorFormKeys.settings,
      ContactEditorSettingFormFields.ErrorMessages
    )

    const contactRef = useRef(contact)

    useEffect(() => {
      contactRef.current = contact
    }, [contact])

    const errorMessagesRef = useRef<string[]>(errorMessages ?? [])
    const [, setIsLoading] = useGetFormDataField<LoadingState>(
      ContactEditorFormKeys.settings,
      ContactEditorSettingFormFields.IsLoading
    )

    useEffect(() => {
      errorMessagesRef.current = errorMessages ?? []
    }, [errorMessages])

    const partnerIsSource = useMemo(
      () => getPartnerIdSource(contact.partnerId),
      [contact.partnerId]
    )
    const [relinkingFormData] = useGetFormData<Record<string, string>>(formKey)

    const [newPartnerId, , isNewPartnerIdModified] = useGetFormDataField<string>(
      formKey,
      'newPartnerId'
    )
    const resolvedNewPartnerId = isOnboardingVariant
      ? newPartnerId || contact.newPartnerId
      : newPartnerId

    const isContactRelinking = Boolean(
      contact.newPartnerId && contact.workflowStatus && contact.newPartnerId === newPartnerId
    )
    const [relinkingComment, updateRelinkingComment] = useGetFormDataField<string>(
      formKey,
      'relinkingComment'
    )
    const modifiedValues = useMemo(
      () => relinkingFormData.modifiedFields.filter((field) => field !== 'currentPartner'),
      [relinkingFormData.modifiedFields]
    )
    const hasModifiedValues = modifiedValues.length > 0
    const resolvedRequestEntryId = requestContact.entryId375 ?? requestContact.entryId
    const onSaveAsDraft = contact.source?.startsWith('Onboarding')
      ? () => onApplyHandler({ workflowStatus: ContactWorkflowStatus.Draft })
      : undefined

    const getRelinkingFormValues = useCallback(
      (newHpRoles?: string[]) => {
        const formValues: Record<string, string | string[]> = Object.fromEntries(
          Object.entries(relinkingFormData.formData).filter(([field]) => field !== 'currentPartner')
        )

        if (newHpRoles) {
          formValues.hpRoles = newHpRoles
        }

        return formValues
      },
      [relinkingFormData.formData]
    )

    const buildUpdatedContact = useCallback(
      (
        nextContact: IContact,
        formValues: Record<string, string | string[]>,
        responseData: {
          modifiedValues?: string
        } & Record<string, unknown>
      ): IContact => ({
        ...nextContact,
        ...formValues,
        ...responseData,
        modifiedValues: responseData.modifiedValues
          ? mapContactModifiedValues(responseData.modifiedValues.split(',') as (keyof I375Form)[])
          : modifiedValues,
        modified: new Date().toISOString(),
      }),
      [modifiedValues]
    )

    const appendErrorMessages = useCallback(
      (messages: string[]) => {
        setErrorMessages(Array.from(new Set([...(errorMessagesRef.current ?? []), ...messages])))
      },
      [setErrorMessages]
    )

    const onApplyHandler = useCallback(
      async ({ newHpRoles, workflowStatus }: IOnApplyProps): Promise<THandlerResult> => {
        const formValues = getRelinkingFormValues(newHpRoles)
        const resolvedWorkflowStatus = workflowStatus || ContactWorkflowStatus.New
        const nextContact: IContact = {
          ...contact,
          entryId: resolvedRequestEntryId,
          ...formValues,
          contactId: contact.contactId,
          modifiedValues,
          workflowStatus: resolvedWorkflowStatus,
          source: appSource,
        }

        try {
          setIsLoading({
            isLoading: true,
            message:
              resolvedWorkflowStatus === ContactWorkflowStatus.Draft
                ? 'Saving draft...'
                : isNewPartnerIdModified
                  ? 'Relinking contact...'
                  : 'Updating contact...',
          })

          const response = await replacementContact(nextContact, { forceRoles: true })

          if (!response) {
            appendErrorMessages(['Something went wrong!'])
            return { hasError: true }
          }

          switch (response.status) {
            case OnboardingResponseStatus.Fail: {
              appendErrorMessages(getJSendErrorMessages(response))
              return { hasError: true }
            }
            case OnboardingResponseStatus.Error: {
              appendErrorMessages(response.message ? [response.message] : [])
              return { hasError: true }
            }
            case OnboardingResponseStatus.Success: {
              const updatedContact = buildUpdatedContact(nextContact, formValues, response.data)

              if (contact.onboardingRequestId) {
                dispatch(
                  contactApi.getContactsWithMultiplePartners({
                    requestId: contact.onboardingRequestId,
                  })
                )
              }

              await onContactUpdate(updatedContact)
              updateActualData({ ...actualData, contact: updatedContact })
              return { hasError: false }
            }
            default: {
              appendErrorMessages(['Something went wrong!'])
              return { hasError: true }
            }
          }
        } catch {
          appendErrorMessages(['Unexpected error occurred'])
          return { hasError: true }
        } finally {
          setIsLoading({ isLoading: false, message: '' })
        }
      },
      [
        actualData,
        appendErrorMessages,
        buildUpdatedContact,
        contact,
        dispatch,
        getRelinkingFormValues,
        isNewPartnerIdModified,
        modifiedValues,
        onContactUpdate,
        resolvedRequestEntryId,
        appSource,
        setIsLoading,
        updateActualData,
      ]
    )

    const onCancelHandler = useCallback(async () => {
      dispatch(resetAllModifiedDataByKey(formKey))
    }, [dispatch, formKey])

    const checkRelinkingStepComment = useCallback(async () => {
      const formValidation = FormValidationBuilder.build(formKey)
      const validationResult = await formValidation.call('relinkingComment', relinkingComment)

      if (validationResult) {
        const { field, message } = validationResult
        const nextMessage = `${field}: ${message}`

        setErrorMessages(Array.from(new Set([...errorMessagesRef.current, nextMessage])))
      }
    }, [relinkingComment, setErrorMessages, formKey])

    const onChangeRelinkingPartnerId = useCallback(
      async (value: string, item?: IForm) => {
        if (!value) {
          updateRelinkingComment('')
          dispatch(
            updateInvalidData({
              key: formKey,
              field: 'relinkingComment',
              validationMessage: '',
            })
          )
          return
        }

        if (item) {
          const mappedPartner = mapPartnerFromPartnerAllDataRegions(item)
          const criticalMessage = await checkCriticalRelinkingPoints(
            mappedPartner,
            contactRef.current
          )
          setRelinkingCriticalMessage((prev) => (prev === criticalMessage ? prev : criticalMessage))
        }

        await checkRelinkingStepComment()
      },
      [checkRelinkingStepComment, dispatch, formKey, updateRelinkingComment]
    )

    const closeCriticalRelinkingModal = useCallback(() => {
      setRelinkingCriticalMessage(null)
      dispatch(
        resetModifiedDataByField({
          formKey: formKey,
          field: 'newPartnerId',
        })
      )
    }, [dispatch, formKey])

    const renderPartnerOption = useCallback((item: IForm) => {
      return (
        <div>
          {item.CommunicationName} &lt;{item.PartnerID}&gt;
        </div>
      )
    }, [])

    return (
      <ContactEditableContainer
        contact={contact}
        contactFlags={contactFlags}
        onApply={onApplyHandler}
        onSaveAsDraft={onSaveAsDraft}
        onCancel={onCancelHandler}
        onUpdate={onContactUpdate}
        hasModifiedValues={hasModifiedValues}
      >
        <FieldGroup title="Contact info" size={0} classNames={{ groupFields: styles.groupFields }}>
          <FormInput
            formKey={formKey}
            defaultValue={contact.firstName}
            field="firstName"
            label="First Name"
            isReadOnly={isOnboardingVariant ? isCurrentMenuItemReadOnly : true}
          />
          <FormInput
            formKey={formKey}
            defaultValue={contact.lastName}
            field="lastName"
            label="Last Name"
            isReadOnly={isOnboardingVariant ? isCurrentMenuItemReadOnly : true}
          />
          <FormInput
            formKey={formKey}
            defaultValue={contact.email}
            field="email"
            label="Email"
            isReadOnly={isOnboardingVariant ? isCurrentMenuItemReadOnly : true}
          />
          <FormInput
            formKey={formKey}
            defaultValue={contact.partnerId}
            field="currentPartner"
            label="Current Partner"
            isReadOnly
          />
          {isOnboardingVariant ? (
            <FormInput
              formKey={formKey}
              defaultValue={contact.newPartnerId}
              field="newPartnerId"
              label="New Partner"
              isReadOnly
            />
          ) : (
            <FieldWrapperWithChanges hasChanges={Boolean(contact.newPartnerId)}>
              <FormDropdown
                source={partnerIsSource}
                caption="PartnerID"
                formKey={formKey}
                defaultValue={contact.newPartnerId}
                field="newPartnerId"
                label="New Partner"
                isReadOnly={isCurrentMenuItemReadOnly}
                onChange={onChangeRelinkingPartnerId}
                renderFunction={renderPartnerOption}
                fieldsToSearch={PARTNER_FIELDS_TO_SEARCH}
              />
            </FieldWrapperWithChanges>
          )}
          <FieldWrapperWithChanges hasChanges={Boolean(contact.relinkingComment)}>
            <FormInput
              formKey={formKey}
              defaultValue={contact.relinkingComment}
              field="relinkingComment"
              label="Comment"
              isDisabled={!resolvedNewPartnerId || (!isOnboardingVariant && isContactRelinking)}
              isReadOnly={isCurrentMenuItemReadOnly}
              isRequired={contact.region === Region.APJ && !isContactRelinking}
              validationType={ValidationType.ONCHANGE}
            />
          </FieldWrapperWithChanges>
        </FieldGroup>
        <ContactRelinkingValidationMessage
          text={relinkingCriticalMessage}
          onClose={closeCriticalRelinkingModal}
        />
      </ContactEditableContainer>
    )
  }
)

WrappedOnboardingContactRelinking.displayName = 'WrappedOnboardingContactRelinking'
